import { DateTime } from "luxon";
import { i18n } from "@/lib/i18n";
import type { TeamMember, WorkerAccountStatus } from "../types";

/**
 * Determine the account status of a worker based on their user connection and invitation
 */
export function getWorkerAccountStatus(workerSalon: TeamMember): WorkerAccountStatus {
  // If worker has a user account, they're linked
  if (workerSalon.worker?.user?.id) {
    return "linked";
  }

  // Use the first pending invitation from the worker's invitations array
  const invitation = workerSalon.worker?.invitations?.[0];

  if (invitation) {
    switch (invitation.status) {
      case "PENDING":
        // Check if invitation is expired
        if (invitation.expiresAt && DateTime.fromISO(invitation.expiresAt) < DateTime.now()) {
          return "invitation_expired";
        }
        return "invitation_pending";

      case "EXPIRED":
        return "invitation_expired";

      case "ACCEPTED":
        return "invitation_accepted";

      case "REJECTED":
        return "email_draft";

      default:
        return "email_draft";
    }
  }

  // No invitation exists
  return "no_email";
}

/**
 * Get display information for worker account status
 */
export function getWorkerStatusDisplay(status: WorkerAccountStatus) {
  const t = i18n.getFixedT(null, "settings");

  const statusMap = {
    linked: {
      label: t("workerStatus.linked"),
      color: "bg-green-100 text-green-800 border-green-200",
      icon: "✓",
      description: t("workerStatus.linkedDescription"),
    },
    invitation_pending: {
      label: t("workerStatus.invitationPending"),
      color: "bg-yellow-100 text-yellow-800 border-yellow-200",
      icon: "📧",
      description: t("workerStatus.invitationPendingDescription"),
    },
    invitation_expired: {
      label: t("workerStatus.invitationExpired"),
      color: "bg-red-100 text-red-800 border-red-200",
      icon: "⏰",
      description: t("workerStatus.invitationExpiredDescription"),
    },
    email_draft: {
      label: t("workerStatus.emailDraft"),
      color: "bg-blue-100 text-blue-800 border-blue-200",
      icon: "📝",
      description: t("workerStatus.emailDraftDescription"),
    },
    no_email: {
      label: t("workerStatus.noEmail"),
      color: "bg-gray-100 text-gray-800 border-gray-200",
      icon: "📧",
      description: t("workerStatus.noEmailDescription"),
    },
    invitation_accepted: {
      label: t("workerStatus.invitationAccepted"),
      color: "bg-purple-100 text-purple-800 border-purple-200",
      icon: "⚙️",
      description: t("workerStatus.invitationAcceptedDescription"),
    },
  };

  return statusMap[status];
}

/**
 * Get available actions for a worker based on their status
 */
export function getWorkerActions(_worker: TeamMember, status: WorkerAccountStatus) {
  const baseActions = ["edit", "delete"];

  switch (status) {
    case "linked":
      return [...baseActions];

    case "invitation_pending":
      return [...baseActions, "change_email"];

    case "invitation_expired":
      return [...baseActions, "change_email"];

    case "email_draft":
      return [...baseActions, "send_invitation", "change_email"];

    case "no_email":
      return [...baseActions, "setup_account"];

    case "invitation_accepted":
      return [...baseActions, "view_progress"];

    default:
      return baseActions;
  }
}

/**
 * Check if a worker needs attention (expired invitation, no email, etc.)
 */
export function workerNeedsAttention(_worker: TeamMember, status: WorkerAccountStatus): boolean {
  return status === "invitation_expired" || status === "no_email";
}

/**
 * Get the primary action suggestion for a worker
 */
export function getPrimaryAction(status: WorkerAccountStatus): string | null {
  const t = i18n.getFixedT(null, "settings");

  switch (status) {
    case "no_email":
      return null;

    case "email_draft":
      return t("workerStatus.sendInvitationAction");

    case "invitation_expired":
      return t("workerStatus.changeEmailAction");

    case "invitation_pending":
      return null; // No action needed, waiting for response

    case "linked":
      return null; // All set up

    case "invitation_accepted":
      return null; // In progress

    default:
      return null;
  }
}

/**
 * Sort workers by priority (those needing attention first)
 */
export function sortWorkersByPriority(
  workers: TeamMember[],
  getStatus: (worker: TeamMember) => WorkerAccountStatus
): TeamMember[] {
  return [...workers].sort((a, b) => {
    const statusA = getStatus(a);
    const statusB = getStatus(b);

    const priorityOrder: Record<WorkerAccountStatus, number> = {
      invitation_expired: 1,
      no_email: 2,
      email_draft: 3,
      invitation_pending: 4,
      invitation_accepted: 5,
      linked: 6,
    };

    const priorityA = priorityOrder[statusA] || 999;
    const priorityB = priorityOrder[statusB] || 999;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // If same priority, sort by name
    const nameA = a.worker?.displayName || "";
    const nameB = b.worker?.displayName || "";
    return nameA.localeCompare(nameB);
  });
}
